// Stingers (§13.3): short sequenced phrases played by the music engine in the current key, on the
// sampled instruments, starting on a quantized grid point. The KO ta-da resolves on the key root.
import { pc } from './theory';
import type { SampledInstrument, Sampler } from './sampler';
import type { DrumId } from './songTypes';

export type StingerId =
  | 'koTada' | 'revenge' | 'bounty' | 'oops' | 'clutch' | 'roundStart' | 'suddenDeathSiren'
  | 'goldenFanfare' | 'matchPoint' | 'gameStinger' | 'victory';

export const STINGER_IDS: readonly StingerId[] = [
  'koTada', 'revenge', 'bounty', 'oops', 'clutch', 'roundStart', 'suddenDeathSiren', 'goldenFanfare', 'matchPoint', 'gameStinger', 'victory',
];

export interface StingerEnv {
  sampler: Sampler;
  dest: AudioNode;
  /** Context time of beat 0 of the phrase. */
  at: number;
  /** Any MIDI note with the key's root pitch class. */
  tonic: number;
  /** Seconds per beat. */
  beat: number;
  minor: boolean;
  detune: number;
  /** One-shot SFX (announcer words, siren) at a context time. */
  sfx(key: 'ann.bonk' | 'ann.suddenDeath' | 'ann.matchPoint' | 'ann.game' | 'siren', at: number): void;
}

export interface StingerDef {
  caption: string;
  /** Instruments and drums it uses (pre-render list). */
  instruments: readonly SampledInstrument[];
  drums: readonly DrumId[];
  play(e: StingerEnv): number;
}

/** Note helper: root-relative semitones at octave `oct` (4 → 60..71). */
function root(e: StingerEnv, oct: number): number {
  return 12 * (oct + 1) + pc(e.tonic);
}

/** Stinger level trim, dB (stingers sit on top of the score; the mixer adds its own bus trim). */
const TRIM_DB = -3;

function note(e: StingerEnv, inst: SampledInstrument, midi: number, beat: number, beats: number, vel: number, db = 0): void {
  e.sampler.note(inst, midi, e.at + beat * e.beat, Math.max(0.05, beats * e.beat), vel, e.dest, e.detune, db + TRIM_DB);
}

function drum(e: StingerEnv, id: DrumId, beat: number, vel: number, db = 0): void {
  e.sampler.drum(id, e.at + beat * e.beat, vel, e.dest, db + TRIM_DB);
}

/** A chord, lightly rolled; each voice sits lower the more voices there are. */
function chord(e: StingerEnv, inst: SampledInstrument, midis: readonly number[], beat: number, beats: number, vel: number, db = 0): void {
  const perVoice = -10 * Math.log10(Math.max(1, midis.length));
  midis.forEach((m, i) => note(e, inst, m, beat + (i * 0.012) / e.beat, beats, vel, db + perVoice + 1));
}

export const STINGERS: Readonly<Record<StingerId, StingerDef>> = {
  // "ta-DA!": the 5th on the grid point, then the root (power chord + bell) on the next 8th.
  koTada: {
    caption: '♪ Ta-da!', instruments: ['brass', 'bell', 'timpani', 'glock'], drums: ['crash'],
    play(e) {
      const r = root(e, 4);
      note(e, 'brass', r + 7, 0, 0.35, 0.8);
      chord(e, 'brass', [r, r + 7, r + 12], 0.5, 1.5, 1);
      note(e, 'bell', r + 24, 0.5, 2, 0.9);
      note(e, 'glock', r + 24, 0.5, 1.5, 0.7, -3);
      note(e, 'timpani', root(e, 2), 0.5, 2, 0.9);
      drum(e, 'crash', 0.5, 0.7, -6);
      return 2.5 * e.beat;
    },
  },
  // "dun-dun-DUN": ♭6 → 5 → root, low brass with timpani.
  revenge: {
    caption: '♪ REVENGE!', instruments: ['brass', 'timpani'], drums: ['crash'],
    play(e) {
      const r = root(e, 3);
      chord(e, 'brass', [r + 8, r + 15], 0, 0.3, 0.85);
      chord(e, 'brass', [r + 7, r + 14], 0.5, 0.3, 0.85);
      chord(e, 'brass', [r, r + 7, r + 12], 1, 1.5, 1);
      note(e, 'timpani', root(e, 2) + 8, 0, 0.5, 0.7);
      note(e, 'timpani', root(e, 2) + 7, 0.5, 0.5, 0.7);
      note(e, 'timpani', root(e, 2), 1, 2, 1);
      drum(e, 'crash', 1, 0.8, -4);
      return 3 * e.beat;
    },
  },
  // "cha-ching": a pentatonic bell run up to the octave (the minor pentatonic in minor keys).
  bounty: {
    caption: '♪ BOUNTY!', instruments: ['bell', 'glock', 'sparkle'], drums: ['tambourine'],
    play(e) {
      const r = root(e, 5);
      (e.minor ? [0, 3, 5, 7, 10, 12] : [0, 2, 4, 7, 9, 12]).forEach((iv, i) => note(e, 'bell', r + iv, i * 0.125, 0.5, 0.75 + i * 0.04));
      note(e, 'glock', r + 12, 0.75, 1.5, 0.9);
      note(e, 'sparkle', r + 24, 0.75, 1, 0.8);
      drum(e, 'tambourine', 0.75, 0.9);
      return 2.25 * e.beat;
    },
  },
  // Sad trombone: three descending notes, the last one drooping.
  oops: {
    caption: '♪ Sad trombone', instruments: ['trombone'], drums: [],
    play(e) {
      const r = root(e, 3);
      note(e, 'trombone', r + 7, 0, 0.6, 0.85);
      note(e, 'trombone', r + 6, 0.75, 0.6, 0.85);
      note(e, 'trombone', r + 5, 1.5, 2, 0.95);
      return 3.75 * e.beat;
    },
  },
  // A rising glock arpeggio into a bright power chord.
  clutch: {
    caption: '♪ CLUTCH!', instruments: ['glock', 'brass', 'sparkle'], drums: ['crash'],
    play(e) {
      const r = root(e, 5);
      const third = e.minor ? 3 : 4;
      [0, third, 7, 12].forEach((iv, i) => note(e, 'glock', r + iv, i * 0.125, 0.6, 0.85));
      chord(e, 'brass', [root(e, 4), root(e, 4) + 7, root(e, 4) + 12], 0.5, 1, 0.9);
      note(e, 'sparkle', r + 12, 0.5, 1, 0.7);
      drum(e, 'crash', 0.5, 0.6, -6);
      return 2 * e.beat;
    },
  },
  // "BONK!" plus a drum hit on the downbeat of bar 0.
  roundStart: {
    caption: 'Announcer: BONK!', instruments: [], drums: ['kick', 'crash', 'snare'],
    play(e) {
      e.sfx('ann.bonk', e.at);
      drum(e, 'kick', 0, 1, 2);
      drum(e, 'snare', 0, 0.9, -2);
      drum(e, 'crash', 0, 1, -2);
      return e.beat;
    },
  },
  suddenDeathSiren: {
    caption: 'Siren: SUDDEN DEATH!', instruments: ['timpani', 'brass'], drums: ['crash'],
    play(e) {
      e.sfx('siren', e.at);
      e.sfx('ann.suddenDeath', e.at + 0.35);
      // In the Sudden Death key (the director passes the tonic sounding just inside it, +1).
      const t = root(e, 2);
      for (let i = 0; i < 8; i++) note(e, 'timpani', t, 2 + i * 0.25, 0.3, 0.4 + i * 0.07);
      chord(e, 'brass', [root(e, 3), root(e, 3) + 7, root(e, 4)], 4, 1, 0.9);
      drum(e, 'crash', 4, 0.9, -4);
      return 5 * e.beat;
    },
  },
  // Golden Round: "ta-ta-ta TAAA-TAAAA" on the brass with timpani and a crash.
  goldenFanfare: {
    caption: '♪ Golden fanfare', instruments: ['brass', 'timpani', 'glock'], drums: ['crash', 'kick'],
    play(e) {
      e.sfx('ann.bonk', e.at);
      const r = root(e, 4);
      const third = e.minor ? 3 : 4;
      drum(e, 'kick', 0, 1, 2);
      drum(e, 'crash', 0, 1, -2);
      chord(e, 'brass', [r, r + third, r + 7], 0, 0.3, 0.85);
      chord(e, 'brass', [r, r + third, r + 7], 1 / 3, 0.3, 0.8);
      chord(e, 'brass', [r, r + third, r + 7], 2 / 3, 0.3, 0.85);
      chord(e, 'brass', [r + 2, r + 7, r + 11], 1, 1, 0.95);
      chord(e, 'brass', [r + third, r + 7, r + 12], 2, 2, 1);
      note(e, 'glock', r + 24, 2, 2, 0.8);
      note(e, 'timpani', root(e, 2), 2, 2, 1);
      drum(e, 'crash', 2, 0.8, -4);
      return 4 * e.beat;
    },
  },
  matchPoint: {
    caption: '♪ MATCH POINT', instruments: ['timpani', 'brass'], drums: ['crash'],
    play(e) {
      e.sfx('ann.matchPoint', e.at);
      const t = root(e, 2);
      for (let i = 0; i < 6; i++) note(e, 'timpani', t, i * 0.166, 0.25, 0.45 + i * 0.09);
      chord(e, 'brass', [root(e, 3), root(e, 3) + 3, root(e, 3) + 7], 1, 2, 0.95);
      drum(e, 'crash', 1, 0.7, -6);
      return 3 * e.beat;
    },
  },
  gameStinger: {
    caption: '♪ GAME!', instruments: ['brass', 'timpani', 'glock'], drums: ['crash', 'kick'],
    play(e) {
      e.sfx('ann.game', e.at);
      const r = root(e, 4);
      drum(e, 'kick', 0, 1, 3);
      drum(e, 'crash', 0, 1, 0);
      chord(e, 'brass', [root(e, 3), r, r + 7, r + 12], 0, 2.5, 1, 2);
      note(e, 'timpani', root(e, 2), 0, 2.5, 1);
      [0, 4, 7, 12, 16].forEach((iv, i) => note(e, 'glock', r + 12 + iv, 0.5 + i * 0.125, 0.6, 0.7));
      chord(e, 'brass', [r + 4, r + 7, r + 12, r + 16], 2, 2, 1, 1);
      drum(e, 'crash', 2, 0.9, -2);
      return 4.5 * e.beat;
    },
  },
  victory: {
    caption: '♪ Victory!', instruments: ['brass', 'timpani', 'glock', 'tuba'], drums: ['crash', 'kick', 'snare'],
    play(e) {
      const r = root(e, 4);
      const mel: readonly (readonly [number, number, number])[] = [[7, 0, 0.5], [12, 0.5, 0.5], [16, 1, 1], [14, 2, 0.5], [12, 2.5, 0.5], [16, 3, 1], [19, 4, 3]];
      for (const [iv, b, d] of mel) note(e, 'brass', r + iv - 12, b, d, 0.95);
      const under: readonly (readonly [number, number])[] = [[0, 0], [5, 2], [0, 4]];
      for (const [iv, b] of under) {
        chord(e, 'brass', [r - 12 + iv, r - 5 + iv, r + iv], b, 2, 0.75, -3);
        note(e, 'tuba', root(e, 2) + iv, b, 1.5, 0.9);
        drum(e, 'kick', b, 0.9);
      }
      for (let b = 1; b < 4; b += 2) drum(e, 'snare', b, 0.7);
      note(e, 'timpani', root(e, 2), 4, 3, 1);
      note(e, 'glock', r + 24, 4, 3, 0.8);
      drum(e, 'crash', 0, 0.8, -4);
      drum(e, 'crash', 4, 1, -2);
      return 7 * e.beat;
    },
  },
};
